import { useState } from 'react';
import { Alert, Button, Group, Select, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import { Link, useNavigate, useParams } from 'react-router';
import {
  KURUL_TOPLANTI_DURUM_ETIKETI,
  KURUL_TOPLANTI_TIP_ETIKETI,
  KURUL_UYE_TIPI_ETIKETI,
} from '@isg/validation';
import type { KurulDetay } from '@isg/validation';
import {
  BOS_TOPLANTI_SUZGECI,
  etkinToplantiSuzgeciSayisi,
  toplantiListesiCiktiYolu,
  useCiktiIndir,
  useKurul,
  useKurulToplantilari,
} from '../api/queries';
import type { ToplantiListeSuzgeci } from '../api/queries';
import { TEHLIKE_SINIFI_ETIKET } from '../i18n/tr';
import { UyelerBolumu } from './kurul-parts';
import { QueryStatus } from './parts';
import { Bolum, KayitDetayi, Rozet, Sayfalama, SuzgecGostergesi } from '../sozlesme';
import { ANLAM } from '../theme';
import { tarihGoster, tamSayiGoster } from '../lib/format';

/**
 * KURUL DETAYI (Kapsam §28; sözleşme P5 · P9 · P11): künye (KPI türetilmiş —
 * C10) · olağanüstü önerisi (C8, sistem önerir) · üyeler (görevlendirmeden)
 * · toplantı listesi (P4). "Toplantı planla" ayrı sayfa (P1, E1).
 */
export function KurulDetayScreen(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const kurul = useKurul(id);
  if (!kurul.isSuccess) return <QueryStatus queries={[kurul]} kayit />;
  const k = kurul.data;
  return (
    <KayitDetayi
      testId="kurul-detay"
      geriYol="/isg-kurulu"
      geriEtiket="İSG kurulu"
      baslik={`${k.workplaceAd} İSG kurulu`}
      ustBilgi={
        <Group gap="xs">
          <Rozet anlam={k.zorunlu ? 'olumlu' : 'bilgi'} testId="kurul-zorunluluk">
            {k.zorunlu ? 'Zorunlu' : 'Zorunlu değil (gönüllü)'}
          </Rozet>
          {k.sonrakiVade && (
            <Rozet anlam={k.gecikmeGunu > 0 ? 'gecikmis' : 'bilgi'} testId="kurul-vade">
              Sonraki vade {tarihGoster(k.sonrakiVade)}
              {k.gecikmeGunu > 0 ? ` — ${k.gecikmeGunu} gün gecikmiş` : ''}
            </Rozet>
          )}
          <Rozet anlam={k.acikKararSayisi > 0 ? 'dikkat' : 'bilgi'} testId="kurul-acik-karar">
            {k.acikKararSayisi} açık karar
          </Rozet>
        </Group>
      }
      eylemler={
        k.yetkiler.yazabilir && k.id ? (
          <Button
            data-testid="kurul-toplanti-planla"
            component={Link}
            to={`/isg-kurulu/${k.id}/toplanti/yeni`}
          >
            Toplantı planla
          </Button>
        ) : undefined
      }
    >
      <Kunye k={k} />
      {k.olaganustuOnerileri.length > 0 && (
        <Alert color={ANLAM.dikkat} data-testid="kurul-olaganustu-onerisi">
          <Text size="sm" fw={600}>
            Olağanüstü toplantı önerisi
          </Text>
          {k.olaganustuOnerileri.map((o) => (
            <Text size="sm" key={o.incidentId}>
              <Link to={`/olaylar/${o.incidentId}`}>{o.takipNo}</Link> — {tarihGoster(o.olayTarihi)}{' '}
              tarihli ağır kaza sonrası olağanüstü toplantı yapılmadı.
            </Text>
          ))}
        </Alert>
      )}
      <UyelerBolumu kurul={k} />
      <ToplantilarBolumu kurul={k} />
    </KayitDetayi>
  );
}

function Kunye({ k }: { k: KurulDetay }): React.JSX.Element {
  return (
    <Bolum baslik="Künye" testId="kurul-kunye">
      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="sm">
        <KunyeAlani etiket="İşyeri" deger={`${k.companyAd} — ${k.workplaceAd}`} />
        <KunyeAlani
          etiket="Tehlike sınıfı"
          deger={TEHLIKE_SINIFI_ETIKET[k.tehlikeSinifi] ?? k.tehlikeSinifi}
        />
        <KunyeAlani
          etiket="Çalışan sayısı"
          deger={tamSayiGoster(k.calisanSayisi)}
          testId="kurul-kunye-calisan"
        />
        <KunyeAlani
          etiket="Toplantı sıklığı"
          deger={`${k.siklikAy} ayda bir (tavan ${k.siklikTavaniAy})${k.siklikKararNotu ? ` — ${k.siklikKararNotu}` : ''}`}
          testId="kurul-kunye-siklik"
        />
        <KunyeAlani
          etiket="Sekreter"
          deger={k.sekreterUyeTipi ? KURUL_UYE_TIPI_ETIKETI[k.sekreterUyeTipi] : '—'}
        />
        <KunyeAlani etiket="Kuruluş" deger={k.kurulusTarihi ? tarihGoster(k.kurulusTarihi) : '—'} />
        <KunyeAlani
          etiket="Son olağan toplantı"
          deger={k.sonOlaganToplantiTarihi ? tarihGoster(k.sonOlaganToplantiTarihi) : '—'}
          testId="kurul-kunye-son-toplanti"
        />
        <KunyeAlani
          etiket="Kısa süreli iş işareti"
          deger={k.kisaSureliIs ? 'Var (6 aydan kısa)' : 'Yok'}
        />
      </SimpleGrid>
    </Bolum>
  );
}

function KunyeAlani({
  etiket,
  deger,
  testId,
}: {
  etiket: string;
  deger: string;
  testId?: string;
}): React.JSX.Element {
  return (
    <Stack gap={2} data-testid={testId}>
      <Text size="xs" c="dimmed">
        {etiket}
      </Text>
      <Text size="sm">{deger}</Text>
    </Stack>
  );
}

function ToplantilarBolumu({ kurul }: { kurul: KurulDetay }): React.JSX.Element {
  const [suzgec, setSuzgec] = useState<ToplantiListeSuzgeci>(BOS_TOPLANTI_SUZGECI);
  const liste = useKurulToplantilari(kurul.id ?? undefined, suzgec);
  const indir = useCiktiIndir();
  const navigate = useNavigate();
  const degistir = (yama: Partial<ToplantiListeSuzgeci>): void =>
    setSuzgec((s) => ({ ...s, ...yama, sayfa: 1 }));
  return (
    <Bolum baslik="Toplantılar" testId="kurul-toplantilar">
      <Group mb="xs" align="flex-end">
        <Select
          size="xs"
          label="Tip"
          data-testid="kurul-toplanti-tip-suzgeci"
          placeholder="Tümü"
          clearable
          value={suzgec.tip ?? null}
          onChange={(v) => degistir({ tip: (v as ToplantiListeSuzgeci['tip']) ?? undefined })}
          data={Object.entries(KURUL_TOPLANTI_TIP_ETIKETI).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <Select
          size="xs"
          label="Durum"
          data-testid="kurul-toplanti-durum-suzgeci"
          placeholder="Tümü"
          clearable
          value={suzgec.durum ?? null}
          onChange={(v) => degistir({ durum: (v as ToplantiListeSuzgeci['durum']) ?? undefined })}
          data={Object.entries(KURUL_TOPLANTI_DURUM_ETIKETI).map(([value, label]) => ({
            value,
            label,
          }))}
        />
        <SuzgecGostergesi
          etkinSayisi={etkinToplantiSuzgeciSayisi(suzgec)}
          onTemizle={() => setSuzgec(BOS_TOPLANTI_SUZGECI)}
        />
        <Button
          size="xs"
          variant="default"
          data-testid="kurul-toplanti-excel"
          loading={indir.isPending}
          onClick={() =>
            kurul.id &&
            indir.mutate({
              yol: toplantiListesiCiktiYolu(kurul.id, suzgec),
              yedekAd: 'kurul-toplantilari.xlsx',
            })
          }
        >
          Excel
        </Button>
      </Group>
      <QueryStatus queries={[liste]} />
      {liste.isSuccess && (
        <>
          <Table striped highlightOnHover data-testid="kurul-toplanti-tablosu">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Takip no</Table.Th>
                <Table.Th>Tarih</Table.Th>
                <Table.Th>Tip</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th>Yeter sayı</Table.Th>
                <Table.Th>Gündem</Table.Th>
                <Table.Th>Karar</Table.Th>
                <Table.Th>Açık karar</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {liste.data.satirlar.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text size="sm" c="dimmed">
                      Toplantı yok.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {liste.data.satirlar.map((t) => (
                <Table.Tr
                  key={t.id}
                  data-testid={`kurul-toplanti-satiri-${t.takipNo}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/isg-kurulu/toplanti/${t.id}`)}
                >
                  <Table.Td>
                    <Link to={`/isg-kurulu/toplanti/${t.id}`}>{t.takipNo}</Link>
                  </Table.Td>
                  <Table.Td>
                    {tarihGoster(t.tarih)}
                    {t.baslangicSaat ? ` ${t.baslangicSaat}` : ''}
                  </Table.Td>
                  <Table.Td>{KURUL_TOPLANTI_TIP_ETIKETI[t.tip]}</Table.Td>
                  <Table.Td>
                    <Rozet
                      anlam={
                        t.durum === 'planlandi'
                          ? 'dikkat'
                          : t.durum === 'yapildi'
                            ? 'olumlu'
                            : 'engel'
                      }
                    >
                      {KURUL_TOPLANTI_DURUM_ETIKETI[t.durum]}
                    </Rozet>
                  </Table.Td>
                  <Table.Td>
                    {t.yeterSayi.katilan}/{t.yeterSayi.uyeTamSayisi}
                  </Table.Td>
                  <Table.Td>{t.gundemSayisi}</Table.Td>
                  <Table.Td>{t.kararSayisi}</Table.Td>
                  <Table.Td>{t.acikKararSayisi}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Sayfalama
            sayfa={liste.data.sayfa}
            sayfaBoyutu={liste.data.sayfaBoyutu}
            toplam={liste.data.toplam}
            onDegis={(sayfa) => setSuzgec((s) => ({ ...s, sayfa }))}
            testId="kurul-toplanti-sayfalama"
          />
        </>
      )}
    </Bolum>
  );
}
